// src/components/CarFilterBar.tsx

import {
  Box,
  Button,
  TextField,
  Menu,
  MenuItem,
  IconButton,
  Typography,
  Slider,
  Stack,
  InputAdornment,
  useTheme,
  useMediaQuery,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import SortIcon from '@mui/icons-material/Sort';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt';
import AppsIcon from '@mui/icons-material/Apps';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Custom Components
import RightDrawer from './RightDrawer';
import { RangeFilterMenu } from './RangeFilterMenu';
import axiosInstance from '../services/axiosInstance';
import { encodeId, decodeId } from '../utils/idEncoder';

// --- TYPES ---
type CarFilterBarProps = {
  viewMode?: 'grid' | 'list';
  onToggleViewMode?: () => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  url?: string;
  onFilterChange?: (key: string, value: string) => void;
};

const ENCRYPTED_KEYS = [
  'vehicleBrandIds',
  'vehicleBodyTypeIds',
  'vehicleFuelTypeIds',
  'vehicleTransmissionTypeIds',
  'vehicleClassIds',
  'dealerIds'
];

interface ApiDataResponse {
  responseCode: string;
  responseData?: {
    dealerCompanyData?: any[];
    vehicleBodyTypeData?: any[];
    vehicleFuelTypeData?: any[];
    vehicleTransmissionTypeData?: any[];
    vehicleBrandData?: any[];
  }
}

const CarFilterBar = ({
  viewMode,
  onToggleViewMode,
  searchQuery,
  onSearchChange,
  url,
  onFilterChange,
}: CarFilterBarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // --- STATE ---
  const [dealerData, setDealerData] = useState<any[]>([]);
  const [fuelTypeData, setvehicleFuelTypeData] = useState<any[]>([]);
  const [TransmissionTypeData, setvehicleTransmissionTypeData] = useState<any[]>([]);
  const [bodyTypesData, setvehicleBodyTypeData] = useState<any[]>([]);
  const [brandData, setvehicleBrandData] = useState<any[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  const [tempSelected, setTempSelected] = useState<{ [key: string]: string[] }>({});
  const [initializedKeys, setInitializedKeys] = useState<Set<string>>(new Set());

  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [sortList, setSortList] = useState<{ key: string; direction: 'asc' | 'desc' }[]>([]);

  const searchParams = new URLSearchParams(location.search);
  const currentStatus = searchParams.get('statusId') || '';

  // --- STYLES ---
  const noScrollbarStyle = {
    overflowX: 'auto',
    display: 'flex',
    gap: 1.5,
    width: '100%',
    paddingBottom: '4px',
    '&::-webkit-scrollbar': { display: 'none' },
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
  };

  // --- SORTING LOGIC ---
  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => setSortAnchorEl(event.currentTarget);
  const handleSortClose = () => setSortAnchorEl(null);

  const handleSortChange = (key: string) => {
    setSortList((prev) => {
      const existing = prev.find((s) => s.key === key);
      let updated: { key: string; direction: 'asc' | 'desc' }[];
      if (existing) {
        const newDirection: 'asc' | 'desc' = existing.direction === 'asc' ? 'desc' : 'asc';
        updated = prev.map((s) => s.key === key ? { ...s, direction: newDirection } : s);
      } else {
        updated = [...prev, { key, direction: 'asc' }];
      }
      const params = new URLSearchParams(location.search);
      params.set('sortKeys', updated.map((s) => s.key).join(','));
      params.set('sortDirs', updated.map((s) => s.direction).join(','));
      params.delete('page');
      navigate(`${url ?? '/vehicle-listing'}?${params.toString()}`);
      return updated;
    });
    handleSortClose();
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const keys = params.get('sortKeys')?.split(',') || [];
    const dirs = params.get('sortDirs')?.split(',') || [];
    if (keys.length > 0) {
      setSortList(keys.map((key, i) => ({ key, direction: (dirs[i] === 'desc' ? 'desc' : 'asc') })));
    }
  }, [location.search]);

  // --- YEAR LOGIC ---
  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 15;
  const maxYear = currentYear;
  
  const [yearRange, setYearRange] = useState<number[]>([
    Number(searchParams.get('minManufacturingYear')) || minYear,
    Number(searchParams.get('maxManufacturingYear')) || maxYear,
  ]);
  const yearMarks = Array.from({ length: 6 }, (_, i) => ({ value: minYear + i * 3, label: (minYear + i * 3).toString() }));

  const handleYearChange = (_: any, newValue: number | number[]) => setYearRange(newValue as number[]);

  const applyYearFilter = () => {
    const [min, max] = yearRange;
    const params = new URLSearchParams(location.search);
    params.set('minManufacturingYear', min.toString());
    params.set('maxManufacturingYear', max.toString());
    params.delete('page');
    navigate(`${url ?? '/vehicle-listing'}?${params.toString()}`);
    handleClose();
  };

  const clearYearFilter = () => {
    const params = new URLSearchParams(location.search);
    params.delete('minManufacturingYear');
    params.delete('maxManufacturingYear');
    navigate(`${url ?? '/vehicle-listing'}?${params.toString()}`);
    setYearRange([minYear, maxYear]);
    handleClose();
  };

  // --- STATUS HANDLER ---
  const handleStatusChange = (event: React.MouseEvent<HTMLElement>, newStatus: string | null) => {
    if (newStatus !== null && onFilterChange) {
      onFilterChange('statusId', newStatus);
    }
  };

  // --- API DATA ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        const payload = { dealerCompanyData: true, vehicleBodyTypeData: true, vehicleFuelTypeData: true, vehicleTransmissionTypeData: true, vehicleBrandData: true };
        const res = await axiosInstance.post("/CustomerVehicle/VehicleMultipleDataList", payload);
        const data = res.data as ApiDataResponse;
        if (data.responseCode === "00" && data.responseData) {
          setvehicleBodyTypeData(data.responseData.vehicleBodyTypeData || []);
          setvehicleFuelTypeData(data.responseData.vehicleFuelTypeData || []);
          setvehicleTransmissionTypeData(data.responseData.vehicleTransmissionTypeData || []);
          setvehicleBrandData(data.responseData.vehicleBrandData || []);
          setDealerData(data.responseData.dealerCompanyData || []);
        }
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, []);

  // --- UI HELPERS ---
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>, filter: string) => {
    event.stopPropagation();
    setSelectedLabel(filter);
    setAnchorEl(event.currentTarget);
    setActiveFilter(filter);
  };
  const handleClose = () => { setAnchorEl(null); setActiveFilter(null); };

  // --- CHECKBOX LOGIC ---
  const getTempSelected = (key: string): string[] => {
    if (!initializedKeys.has(key)) {
      const params = new URLSearchParams(location.search);
      const val = params.get(key);
      const parts = val ? val.split(',') : [];
      let decodedIds = parts;
      if (ENCRYPTED_KEYS.includes(key)) {
        decodedIds = parts.map(hash => decodeId(hash)?.toString() || '').filter(Boolean);
      }
      setTempSelected(prev => ({ ...prev, [key]: decodedIds }));
      setInitializedKeys(prev => new Set(prev).add(key));
      return decodedIds;
    }
    return tempSelected[key] || [];
  };

  const toggleSelection = (key: string, id: string) => {
    setTempSelected(prev => {
      const set = new Set(prev[key] || []);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...prev, [key]: Array.from(set) };
    });
  };

  const applySelection = (key: string) => {
    const rawIds = tempSelected[key] || [];
    let outputString = ENCRYPTED_KEYS.includes(key) ? rawIds.map(id => encodeId(id)).join(',') : rawIds.join(',');
    if (onFilterChange) onFilterChange(key, outputString);
    handleClose();
  };

  const clearSelection = (key: string) => {
    setTempSelected(prev => ({ ...prev, [key]: [] }));
    if (onFilterChange) onFilterChange(key, '');
    handleClose();
  };

  const renderCheckboxFilterMenu = (key: string, data: any[], idField: string, labelField: string) => (
    <Box sx={{ width: 240, maxHeight: 320, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, p: 1, overflowY: 'auto' }}>
        {data.map((item) => {
          const id = item[idField].toString();
          const checked = getTempSelected(key).includes(id);
          return (
            <MenuItem key={id} onClick={() => toggleSelection(key, id)} sx={{ borderRadius: 1, mb: 0.5 }}>
              <input type="checkbox" checked={checked} readOnly style={{ marginRight: 12, transform: 'scale(1.1)' }} />
              <Typography variant="body2">{item[labelField]}</Typography>
            </MenuItem>
          );
        })}
      </Box>
      <Box sx={{ borderTop: '1px solid #eee', p: 1.5, position: 'sticky', bottom: 0, bgcolor: '#fff' }}>
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Button size="small" color="inherit" onClick={() => clearSelection(key)}>Clear</Button>
          <Button size="small" variant="contained" onClick={() => applySelection(key)} sx={{ px: 3 }}>Apply</Button>
        </Stack>
      </Box>
    </Box>
  );

  const filterButtons = ['Brand & Model', 'Body Type', 'Fuel Type', 'Transmission', 'Car Price & Instalment', 'Year', 'Mileage', 'Depreciation'];
  const filterMap: any = {
    'Body Type': { key: 'vehicleBodyTypeIds', data: bodyTypesData, idField: 'vehicleBodyTypeId' },
    'Brand & Model': { key: 'vehicleBrandIds', data: brandData, idField: 'vehicleBrandId' },
    'Fuel Type': { key: 'vehicleFuelTypeIds', data: fuelTypeData, idField: 'vehicleFuelTypeId' },
    'Transmission': { key: 'vehicleTransmissionTypeIds', data: TransmissionTypeData, idField: 'vehicleTransmissionTypeId' },
    'Car Price & Instalment': { key: 'minAskingPrice', getLabel: (p: URLSearchParams) => p.get('minAskingPrice') ? `$${Number(p.get('minAskingPrice')).toLocaleString()}+` : null },
    'Year': { key: 'minManufacturingYear', getLabel: (p: URLSearchParams) => p.get('minManufacturingYear') ? `${p.get('minManufacturingYear')}+` : null },
    'Mileage': { key: 'minMileage', getLabel: (p: URLSearchParams) => p.get('minMileage') ? `${Number(p.get('minMileage')).toLocaleString()}km+` : null },
    'Depreciation': { key: 'minDepreciation', getLabel: (p: URLSearchParams) => p.get('minDepreciation') ? `$${Number(p.get('minDepreciation')).toLocaleString()}+` : null },
  };

  // --- RENDER ---
  return (
    <Box sx={{ width: '100%' }}>
      
      {/* --- ROW 1: Search, Status Toggle, & Utilities --- */}
      <Stack 
        direction={{ xs: 'column', lg: 'row' }} 
        spacing={2} 
        alignItems={{ xs: 'stretch', lg: 'center' }} 
        mb={2.5}
      >
        {/* Search Bar */}
        <Box sx={{ flexGrow: 1, maxWidth: { lg: 400 } }}>
          <TextField
            fullWidth
            placeholder="Search Make or Model..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate(`${location.pathname}?vehicleModel=${searchQuery}`); }}
            size="small"
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
              endAdornment: searchQuery && (
                <IconButton size="small" onClick={() => { onSearchChange(''); navigate(url || '/vehicle-listing'); }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': { 
                borderRadius: 8,
                bgcolor: 'background.paper',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              }
            }}
          />
        </Box>

        {/* Status Toggle - Compact Segmented Control */}
        <ToggleButtonGroup
          value={currentStatus}
          exclusive
          onChange={handleStatusChange}
          size="small"
          aria-label="vehicle status"
          sx={{
            bgcolor: 'background.paper',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            '& .MuiToggleButton-root': { 
              px: 2, 
              py: 0.75, 
              textTransform: 'none', 
              fontWeight: 600,
              border: '1px solid #eee',
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' }
              }
            }
          }}
        >
          <ToggleButton value="">
             All
          </ToggleButton>
          <ToggleButton value="VPB">
            <CheckCircleIcon sx={{ fontSize: 18, mr: 0.5 }} /> Available
          </ToggleButton>
          <ToggleButton value="VSL">
            <DoNotDisturbAltIcon sx={{ fontSize: 18, mr: 0.5 }} /> Sold
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ flexGrow: 1, display: { xs: 'none', lg: 'block' } }} />

        {/* Utilities: Sort, Filter, View, Clear */}
        <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
          <Tooltip title="Sort Options">
            <Button 
              variant="outlined" 
              color="inherit" 
              size="small" 
              startIcon={<SortIcon />} 
              onClick={handleSortClick}
              sx={{ borderRadius: 6, borderColor: '#ddd', textTransform: 'none', fontWeight: 600 }}
            >
              {sortList.length ? 'Sorted' : 'Sort'}
            </Button>
          </Tooltip>

          <Tooltip title="All Filters">
            <Button 
              variant="outlined" 
              color="inherit" 
              size="small" 
              startIcon={<TuneIcon />} 
              onClick={() => setDrawerOpen(true)}
              sx={{ borderRadius: 6, borderColor: '#ddd', textTransform: 'none', fontWeight: 600 }}
            >
              Filters
            </Button>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24, alignSelf: 'center' }} />

          {onToggleViewMode && (
            <Tooltip title={`Switch to ${viewMode === 'grid' ? 'List' : 'Grid'} View`}>
              <IconButton onClick={onToggleViewMode} sx={{ border: '1px solid #eee' }} size="small">
                {viewMode === 'grid' ? <ViewListIcon fontSize="small" /> : <ViewModuleIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Clear All Filters">
            <IconButton color="error" size="small" onClick={() => { setSortList([]); navigate(url || '/vehicle-listing'); }}>
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* --- ROW 2: Quick Filter Pills (Horizontal Scroll) --- */}
      <Box sx={noScrollbarStyle}>
        {filterButtons.map(label => {
          const conf = filterMap[label];
          let subLabel = null;
          let isActive = false;

          if (conf) {
            const params = new URLSearchParams(location.search);
            if (conf.getLabel) subLabel = conf.getLabel(params);
            else {
              const count = (params.get(conf.key)?.split(',') || []).filter(Boolean).length;
              if (count > 0) subLabel = `${count}`;
            }
            if (subLabel) isActive = true;
          }

          const isMenuOpen = selectedLabel === label && Boolean(anchorEl);

          return (
            <Button
              key={label}
              variant={isActive ? "contained" : "outlined"}
              size="small"
              onClick={(e) => handleClick(e, label)}
              endIcon={<KeyboardArrowDownIcon sx={{ ml: -0.5 }} />}
              sx={{
                borderRadius: '20px', // Pill shape
                whiteSpace: 'nowrap',
                flexShrink: 0,
                px: 2,
                py: 0.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                color: isActive ? 'primary.contrastText' : 'text.primary',
                bgcolor: isActive ? 'primary.main' : 'background.paper',
                borderColor: isActive ? 'primary.main' : '#ddd',
                boxShadow: isMenuOpen ? '0 0 0 2px rgba(0,0,0,0.1)' : 'none',
                '&:hover': { 
                  bgcolor: isActive ? 'primary.dark' : '#f5f5f5',
                  borderColor: isActive ? 'primary.dark' : '#ccc',
                }
              }}
            >
              {label}
              {subLabel && (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    bgcolor: isActive ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)',
                    px: 0.8,
                    py: 0.2,
                    borderRadius: 2,
                    fontSize: '0.7rem',
                  }}
                >
                  {subLabel}
                </Box>
              )}
            </Button>
          );
        })}
      </Box>

      {/* --- MENUS & DRAWERS --- */}
      <Menu 
        anchorEl={sortAnchorEl} 
        open={Boolean(sortAnchorEl)} 
        onClose={handleSortClose}
        PaperProps={{ elevation: 3, sx: { minWidth: 160, borderRadius: 2, mt: 1 } }}
      >
        {['askingPrice', 'addDate', 'mileage', 'vehicleModel'].map(k => (
          <MenuItem key={k} onClick={() => handleSortChange(k)} sx={{ fontSize: '0.9rem' }}>
            {k === 'askingPrice' ? 'Price' : k === 'addDate' ? 'Date Listed' : k}
            {sortList.find(s => s.key === k) && (
              sortList.find(s => s.key === k)?.direction === 'asc'
                ? <ArrowUpwardIcon fontSize="small" color="primary" sx={{ ml: 'auto' }} />
                : <ArrowDownwardIcon fontSize="small" color="primary" sx={{ ml: 'auto' }} />
            )}
          </MenuItem>
        ))}
      </Menu>

      <Menu 
        anchorEl={anchorEl} 
        open={Boolean(anchorEl)} 
        onClose={handleClose} 
        PaperProps={{ elevation: 4, sx: { borderRadius: 3, mt: 1 } }}
      >
        {activeFilter === 'Brand & Model' && renderCheckboxFilterMenu('vehicleBrandIds', brandData, 'vehicleBrandId', 'description')}
        {activeFilter === 'Body Type' && renderCheckboxFilterMenu('vehicleBodyTypeIds', bodyTypesData, 'vehicleBodyTypeId', 'description')}
        {activeFilter === 'Fuel Type' && renderCheckboxFilterMenu('vehicleFuelTypeIds', fuelTypeData, 'vehicleFuelTypeId', 'description')}
        {activeFilter === 'Transmission' && renderCheckboxFilterMenu('vehicleTransmissionTypeIds', TransmissionTypeData, 'vehicleTransmissionTypeId', 'description')}
        {activeFilter === 'Year' && (
          <Box p={3} width={280}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">Manufacturing Year</Typography>
            <Box px={1.5} py={3}>
              <Slider value={yearRange} onChange={handleYearChange} valueLabelDisplay="on" min={minYear} max={maxYear} marks={yearMarks} size="small" />
            </Box>
            <Stack direction="row" spacing={1} justifyContent="space-between" mt={1}>
              <Button size="small" color="inherit" onClick={clearYearFilter}>Clear</Button>
              <Button size="small" variant="contained" onClick={applyYearFilter}>Apply</Button>
            </Stack>
          </Box>
        )}
      </Menu>

      <RangeFilterMenu anchorEl={anchorEl} activeFilter={activeFilter || ''} filterKey="Car Price & Instalment" minParamKey="minAskingPrice" maxParamKey="maxAskingPrice" min={1000} max={9999999} label="Price Range ($)" onClose={handleClose} />
      <RangeFilterMenu anchorEl={anchorEl} activeFilter={activeFilter || ''} filterKey="Depreciation" minParamKey="minDepreciation" maxParamKey="maxDepreciation" min={1000} max={100000} label="Depreciation ($)" onClose={handleClose} />
      <RangeFilterMenu anchorEl={anchorEl} activeFilter={activeFilter || ''} filterKey="Mileage" minParamKey="minMileage" maxParamKey="maxMileage" min={0} max={300000} label="Mileage (km)" onClose={handleClose} />

      <RightDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} fuelTypeData={fuelTypeData} TransmissionTypeData={TransmissionTypeData} bodyTypesData={bodyTypesData} brandData={brandData} dealerData={dealerData} />
    </Box>
  );
};

export default CarFilterBar;